"use client";

import { useEffect, useRef, type MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { Bookmark, LogOut, Menu, UserCircle, UserRound } from "lucide-react";
import { usePathname } from "next/navigation";
import { useStore } from "zustand";
import { SuburbSelector } from "@/components/SuburbSelector";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { createHeaderStore } from "@/stores/discovery-stores";
import { useAuthStore } from "@/stores/auth-store";
import { cn } from "@/lib/utils";

const HEADER_SCROLL_OFFSET = 80;

type NavItem = {
  label: string;
  href: string;
  hash?: string;
};

const navItems: NavItem[] = [
  { label: "EXPLORE", href: "/" },
  { label: "MAGAZINE", href: "/#magazine", hash: "magazine" },
  { label: "THIS WEEK", href: "/events" },
  { label: "CONTACT", href: "/#contact", hash: "contact" },
  { label: "LIST A BUSINESS", href: "/business/listing" }
] as const;

const suburbs = ["MALABAR", "MAROUBRA", "LITTLE BAY & SURROUNDS"];
const signupHref = "/auth/signup?redirectTo=/account";

function AccountMenu({ mobile = false }: { mobile?: boolean }) {
  const isLoggedIn = useAuthStore((state) => Boolean(state.user));
  const initialize = useAuthStore((state) => state.initialize);
  const signOut = useAuthStore((state) => state.signOut);

  useEffect(() => {
    void initialize();
  }, [initialize]);

  if (!isLoggedIn) {
    return (
      <Link
        href={signupHref}
        className={cn(
          "flex items-center gap-1.5 text-[11px] font-bold tracking-[0.16em] transition hover:text-brand",
          mobile && "gap-2 py-1"
        )}
      >
        <UserRound className="h-[18px] w-[18px] text-brand" aria-hidden="true" />
        <span>SIGN UP</span>
      </Link>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex items-center gap-1.5 text-[11px] font-bold tracking-[0.16em] transition hover:text-brand",
            mobile && "gap-2 py-1"
          )}
          aria-label="Account actions"
        >
          <UserCircle className="h-[18px] w-[18px] text-brand" aria-hidden="true" />
          <span>ACCOUNT</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="local-buzz-theme w-52 rounded-xl border-border bg-card p-1.5 text-foreground shadow-lift">
        <DropdownMenuItem asChild className="rounded-lg px-3 py-2 text-sm font-semibold">
          <Link href="/account" className="flex items-center gap-2">
            <UserCircle className="h-4 w-4 text-brand" />
            Account
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild className="rounded-lg px-3 py-2 text-sm font-semibold">
          <Link href="/bookmarks" className="flex items-center gap-2">
            <Bookmark className="h-4 w-4 text-brand" />
            Bookmarks
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-border" />
        <DropdownMenuItem
          className="rounded-lg px-3 py-2 text-sm font-semibold text-brand focus:text-brand"
          onSelect={(event) => {
            event.preventDefault();
            void signOut();
          }}
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export function SiteHeader() {
  const pathname = usePathname();
  const storeRef = useRef<ReturnType<typeof createHeaderStore> | null>(null);
  if (!storeRef.current) {
    storeRef.current = createHeaderStore();
  }
  const mobileOpen = useStore(storeRef.current, (state) => state.mobileOpen);
  const locationOpen = useStore(storeRef.current, (state) => state.locationOpen);
  const mobileLocationOpen = useStore(storeRef.current, (state) => state.mobileLocationOpen);
  const setMobileOpen = useStore(storeRef.current, (state) => state.setMobileOpen);
  const setLocationOpen = useStore(storeRef.current, (state) => state.setLocationOpen);
  const setMobileLocationOpen = useStore(storeRef.current, (state) => state.setMobileLocationOpen);

  function handleHashClick(event: MouseEvent<HTMLAnchorElement>, hash?: string) {
    if (!hash || pathname !== "/") return;

    const target = document.getElementById(hash);
    if (!target) return;

    event.preventDefault();
    const top = target.getBoundingClientRect().top + window.scrollY - HEADER_SCROLL_OFFSET;
    window.scrollTo({ top, behavior: "smooth" });
    window.history.pushState(null, "", `#${hash}`);
    setMobileOpen(false);
  }

  return (
    <header className="local-buzz-theme">
      <div className="bg-blush text-blush-foreground">
        <div className="mx-auto grid max-w-[1400px] grid-cols-[auto_1fr_auto] items-center gap-6 px-4 py-3 sm:px-6">
          <Link href="/" aria-label="The Neighbourhood Voice home" className="group flex shrink-0 items-center">
            <Image
              src="/nv-logo.png"
              alt="The Neighbourhood Voice"
              width={1545}
              height={958}
              className="h-11 w-auto object-contain transition-transform duration-300 group-hover:-rotate-1 sm:h-12 lg:h-14"
              priority
            />
          </Link>

          <nav aria-label="Primary navigation" className="hidden items-center justify-center gap-9 text-[12px] font-extrabold tracking-[0.18em] lg:flex">
            {navItems.map((item) => (
              <Link
                key={item.label}
                href={item.href}
                onClick={(event) => handleHashClick(event, item.hash)}
                className="transition hover:text-brand"
              >
                {item.label}
              </Link>
            ))}
          </nav>

          <div className="hidden justify-self-end items-center gap-4 sm:flex lg:gap-5">
            <SuburbSelector open={locationOpen} onOpenChange={setLocationOpen} />
            <AccountMenu />
          </div>

          <button
            type="button"
            aria-label="Menu"
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
            className="justify-self-end rounded-md border border-border p-2 text-blush-foreground transition hover:border-brand hover:text-brand lg:hidden"
          >
            <Menu className="h-5 w-5" aria-hidden="true" />
          </button>
        </div>

        {mobileOpen ? (
          <nav aria-label="Mobile navigation" className="border-t border-black/10 px-4 py-4 lg:hidden">
            <div className="grid gap-3 text-[12px] font-extrabold tracking-[0.18em]">
              <div className="py-1">
                <SuburbSelector open={mobileLocationOpen} onOpenChange={setMobileLocationOpen} compact />
              </div>
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  href={item.href}
                  onClick={(event) => handleHashClick(event, item.hash)}
                  className="py-1 transition hover:text-brand"
                >
                  {item.label}
                </Link>
              ))}
              <AccountMenu mobile />
            </div>
          </nav>
        ) : null}
      </div>

      <div className="bg-brand text-white">
        <div className="no-scrollbar mx-auto flex max-w-[1400px] items-center justify-center gap-8 overflow-x-auto whitespace-nowrap px-4 py-2.5 text-[11px] font-bold tracking-[0.18em] sm:text-[12px]">
          {suburbs.map((suburb, index) => (
            <span key={suburb} className="flex items-center gap-8">
              {index > 0 ? <span className="opacity-60">{"\u2014"}</span> : null}
              <span>{suburb}</span>
            </span>
          ))}
        </div>
      </div>
    </header>
  );
}
